import { ref, computed } from "vue";
import { useDefaultDataStore } from "@/stores/useDefaultDataStore";
import PresupuestoService from "@/services/PresupuestoService";

// Cada columna necesita su propio array real desde el inicio: si a una columna
// vacía se accede vía "items.value[columnId] || []", ese [] es descartable y
// nunca queda guardado en items.value, perdiendo cualquier item soltado ahí.
const groupByEstado = (cards, estadoIds) => {
    const grouped = {};
    estadoIds.forEach((estadoId) => {
        grouped[estadoId] = [];
    });
    cards.forEach((card) => {
        if (!grouped[card.estadoId]) {
            grouped[card.estadoId] = [];
        }
        grouped[card.estadoId].push(card);
    });
    return grouped;
};

// Estado reactivo del kanban compartido por todos los componentes que usan
// este composable (columnas y cards se renderizan desde componentes distintos,
// por lo que este estado no puede vivir dentro de la función useKanbanState).
const items = ref({});
const loading = ref(false);
const processingItemIds = ref(new Set());

export function useKanbanState() {
    const defaultDataStore = useDefaultDataStore();

    // Columnas ordenadas (provienen de la tabla real de estados)
    const columns = computed(() => {
        return defaultDataStore.getEstados;
    });

    // Cargar los presupuestos del backend y agruparlos por estado
    const fetchItems = async (params = {}) => {
        loading.value = true;
        try {
            const cards = await PresupuestoService.kanban(params);
            const estadoIds = defaultDataStore.getEstados.map((estado) => estado.id);
            items.value = groupByEstado(cards, estadoIds);
        } catch (error) {
            console.error("No se pudieron cargar los presupuestos del kanban", error);
        } finally {
            loading.value = false;
        }
    };

    // Obtener items de una columna específica
    const getColumnItems = (columnId) => {
        return items.value[columnId] || [];
    };

    // Marcar/consultar items con una actualización de estado en curso
    const setItemProcessing = (itemId, isProcessing) => {
        const next = new Set(processingItemIds.value);
        if (isProcessing) {
            next.add(itemId);
        } else {
            next.delete(itemId);
        }
        processingItemIds.value = next;
    };

    const isItemProcessing = (itemId) => {
        return processingItemIds.value.has(itemId);
    };

    // Mover item entre columnas (persiste el cambio de estado en el backend)
    const moveItem = async (itemId, sourceColumnId, targetColumnId) => {
        if (!items.value[sourceColumnId]) {
            console.error("Columna origen no existe");
            return false;
        }

        const sourceArray = items.value[sourceColumnId];
        const itemIndex = sourceArray.findIndex((item) => item.id === itemId);

        if (itemIndex === -1) {
            console.error("Item no encontrado");
            return false;
        }

        const movedItem = sourceArray[itemIndex];

        try {
            await PresupuestoService.estadoUpdate(itemId, targetColumnId);
        } catch (error) {
            console.error("No se pudo actualizar el estado del presupuesto", error);
            return false;
        }

        movedItem.estadoId = targetColumnId;

        // Crear nuevos arrays en lugar de mutar los existentes
        items.value = {
            ...items.value,
            [sourceColumnId]: sourceArray.filter((item) => item.id !== itemId),
            [targetColumnId]: [...(items.value[targetColumnId] || []), movedItem],
        };

        return true;
    };

    return {
        items,
        loading,
        columns,
        fetchItems,
        getColumnItems,
        moveItem,
        setItemProcessing,
        isItemProcessing,
    };
}
